import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import AjaxHandler from '../../commons/ajax/AjaxHandler.js';
import Security from '../../commons/security/Security.js';
import Loading from '../ui/Loading.js';
import MonologMessage from './MonologMessage.js';
import swal from 'sweetalert';
import './Monolog.css';

const NIVELES = ['DEBUG', 'INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'];
const NIVELES_DEFAULT = ['INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'];
const PER_PAGE = 200;

// Forzamos TZ Argentina explícita: aunque Monolog escribe con -03:00, la hora mostrada
// debe ser igual aun si el browser o el servidor están en otra TZ. Formatters cacheados
// a nivel módulo para no instanciar en cada celda.
const TZ_AR = 'America/Argentina/Buenos_Aires';
const FMT_FECHA = new Intl.DateTimeFormat('es-AR', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: TZ_AR,
});
const FMT_HORA = new Intl.DateTimeFormat('es-AR', {
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: TZ_AR,
});
const FMT_HORA_CORTA = new Intl.DateTimeFormat('es-AR', {
  hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ_AR,
});

/**
 * Monolog — visualizador de logs del API.
 *
 * Restringido a usuarios internos en ambientes dev (ambientes locales). El backend valida ambas
 * condiciones; si no se cumple, los endpoints responden 403/404 y el componente
 * muestra el mensaje de error correspondiente.
 *
 * Llama a:
 *   GET /api/logs/files
 *   GET /api/logs/file?name=...&level=...&q=...&page=...&perPage=...
 */
export class Monolog extends Component {
  constructor(props) {
    super(props);

    this.ajaxHandler = new AjaxHandler();

    this.state = {
      redirectTo: null,
      files: [],
      filesCargando: false,
      filesError: null,

      archivoSeleccionado: null,
      lineas: [],
      total: 0,
      truncated: false,
      page: 1,
      lineasCargando: false,
      lineasError: null,

      filtroNiveles: NIVELES_DEFAULT.slice(),
      filtroBusqueda: '',
    };

    this.cargarArchivos = this.cargarArchivos.bind(this);
    this.seleccionarArchivo = this.seleccionarArchivo.bind(this);
    this.cargarLineas = this.cargarLineas.bind(this);
    this.handleEliminar = this.handleEliminar.bind(this);
    this.handleBusquedaChange = this.handleBusquedaChange.bind(this);
    this.handleNivelToggle = this.handleNivelToggle.bind(this);
    this.handleBuscar = this.handleBuscar.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);

    this.busquedaTimer = null;
  }

  componentDidMount() {
    if (!Security.isInternal()) {
      this.setState({ redirectTo: '/error' });
      return;
    }
    this.ajaxHandler.subscribe(this);
    this.cargarArchivos();
  }

  componentWillUnmount() {
    if (this.busquedaTimer) {
      clearTimeout(this.busquedaTimer);
      this.busquedaTimer = null;
    }
    this.ajaxHandler.unsubscribe();
  }

  cargarArchivos() {
    var self = this;
    this.setState({ filesCargando: true, filesError: null });
    this.ajaxHandler
      .fetch('/logs/files', { method: 'GET', silent: true })
      .then(function (response) {
        // AjaxHandler rechaza para !2xx, así que acá solo llegan 200. Si en el
        // futuro algún 2xx no-200 llegara, lo tratamos como error de listado.
        if (response.status !== 200) {
          self.setState({
            filesCargando: false,
            filesError: self.mensajeError(response.status, 'monolog.error_listado'),
          });
          return;
        }
        response.json().then(function (data) {
          var lista = data && data.data && data.data.files ? data.data.files : [];
          self.setState({ files: lista, filesCargando: false });
          // Auto-seleccionar el archivo más reciente si todavía no hay uno elegido.
          if (!self.state.archivoSeleccionado && lista.length > 0) {
            self.seleccionarArchivo(lista[0].name);
          }
        });
      })
      .catch(function (err) {
        self.setState({
          filesCargando: false,
          filesError: self.errorMessageFromCatch(err, 'monolog.error_listado'),
        });
      });
  }

  seleccionarArchivo(name) {
    this.setState({ archivoSeleccionado: name, page: 1 }, this.cargarLineas);
  }

  cargarLineas() {
    var self = this;
    var name = this.state.archivoSeleccionado;
    if (!name) {
      return;
    }
    var params = ['name=' + encodeURIComponent(name)];
    var niveles = this.state.filtroNiveles;
    if (niveles && niveles.length > 0) {
      params.push('level=' + encodeURIComponent(niveles.join(',')));
    }
    if (this.state.filtroBusqueda && this.state.filtroBusqueda.length > 0) {
      params.push('q=' + encodeURIComponent(this.state.filtroBusqueda));
    }
    params.push('page=' + this.state.page);
    params.push('perPage=' + PER_PAGE);

    this.setState({ lineasCargando: true, lineasError: null });

    this.ajaxHandler
      .fetch('/logs/file?' + params.join('&'), { method: 'GET', silent: true })
      .then(function (response) {
        // AjaxHandler rechaza para !2xx, así que acá solo llegan 200.
        if (response.status !== 200) {
          self.setState({
            lineasCargando: false,
            lineasError: self.mensajeError(response.status, 'monolog.error_lectura'),
          });
          return;
        }
        response.json().then(function (data) {
          var d = data && data.data ? data.data : {};
          self.setState({
            lineas: d.lines || [],
            total: d.total || 0,
            truncated: !!d.truncated,
            lineasCargando: false,
          });
        });
      })
      .catch(function (err) {
        self.setState({
          lineasCargando: false,
          lineasError: self.errorMessageFromCatch(err, 'monolog.error_lectura'),
        });
      });
  }

  mensajeError(status, idGenerico) {
    var intl = this.props.intl;
    if (status === 403) {
      return intl.formatMessage({
        id: 'monolog.error_no_autorizado',
        defaultMessage: 'No tenés permisos para ver esta sección.',
      });
    }
    if (status === 404) {
      return intl.formatMessage({
        id: 'monolog.error_no_disponible',
        defaultMessage: 'La sección de logs solo está disponible en ambientes de desarrollo (ambientes locales).',
      });
    }
    return intl.formatMessage({ id: idGenerico, defaultMessage: 'Ocurrió un error al cargar los logs.' });
  }

  // AjaxHandler adjunta `err.status` cuando la rejection viene de un fallo HTTP.
  // Sin status => error real de transporte → mostrar "error de conexión".
  errorMessageFromCatch(err, idGenerico) {
    if (err && typeof err.status === 'number') {
      return this.mensajeError(err.status, idGenerico);
    }
    return this.props.intl.formatMessage({
      id: 'monolog.error_conexion',
      defaultMessage: 'No se pudo conectar con el servidor.',
    });
  }

  handleBusquedaChange(e) {
    var valor = e.target.value;
    var self = this;
    this.setState({ filtroBusqueda: valor });
    if (this.busquedaTimer) {
      clearTimeout(this.busquedaTimer);
    }
    this.busquedaTimer = setTimeout(function () {
      self.setState({ page: 1 }, self.cargarLineas);
    }, 350);
  }

  handleNivelToggle(nivel) {
    var actuales = this.state.filtroNiveles.slice();
    var idx = actuales.indexOf(nivel);
    if (idx >= 0) {
      actuales.splice(idx, 1);
    } else {
      actuales.push(nivel);
    }
    var self = this;
    this.setState({ filtroNiveles: actuales, page: 1 }, function () {
      if (self.state.archivoSeleccionado) {
        self.cargarLineas();
      }
    });
  }

  handleBuscar() {
    if (this.state.archivoSeleccionado) {
      this.setState({ page: 1 }, this.cargarLineas);
    }
  }

  handleEliminar(name, event) {
    if (event) {
      event.stopPropagation();
    }
    var self = this;
    var intl = this.props.intl;

    swal({
      title: intl.formatMessage({ id: 'monolog.confirmar_eliminar.titulo', defaultMessage: '¿Eliminar archivo de log?' }),
      text: intl.formatMessage(
        { id: 'monolog.confirmar_eliminar.texto', defaultMessage: 'Se eliminará permanentemente "{name}". Esta acción no se puede deshacer.' },
        { name: name },
      ),
      icon: 'warning',
      buttons: {
        cancel: {
          text: intl.formatMessage({ id: 'monolog.confirmar_eliminar.cancelar', defaultMessage: 'Cancelar' }),
          value: null,
          visible: true,
          className: 'btn btn-secondary',
          closeModal: true,
        },
        confirm: {
          text: intl.formatMessage({ id: 'monolog.confirmar_eliminar.confirmar', defaultMessage: 'Eliminar' }),
          value: true,
          visible: true,
          className: 'btn btn-danger',
          closeModal: true,
        },
      },
      dangerMode: true,
    }).then(function (confirmado) {
      if (!confirmado) {
        return;
      }
      self.ajaxHandler
        .fetch('/logs/file?name=' + encodeURIComponent(name), { method: 'DELETE', silent: true })
        .then(function (response) {
          // AjaxHandler rechaza para !2xx, así que acá solo llegan 200.
          if (response.status !== 200) {
            swal({
              title: intl.formatMessage({ id: 'monolog.eliminar_error', defaultMessage: 'No se pudo eliminar el archivo.' }),
              icon: 'error',
            });
            return;
          }
          // Si era el archivo abierto en el viewer, limpiar el estado.
          var nuevoEstado = { lineasError: null };
          if (self.state.archivoSeleccionado === name) {
            nuevoEstado.archivoSeleccionado = null;
            nuevoEstado.lineas = [];
            nuevoEstado.total = 0;
            nuevoEstado.truncated = false;
            nuevoEstado.page = 1;
          }
          self.setState(nuevoEstado);
          self.cargarArchivos();
        })
        .catch(function (err) {
          // AjaxHandler ya muestra swal para 401/403 con su propio texto. Para 404/5xx
          // (silent suprime el swal genérico) y para errores reales de red, mostramos
          // un swal con mensaje específico.
          var status = err && typeof err.status === 'number' ? err.status : null;
          if (status === 401 || status === 403) {
            return;
          }
          swal({
            title: intl.formatMessage({ id: 'monolog.eliminar_error', defaultMessage: 'No se pudo eliminar el archivo.' }),
            text: status ? self.mensajeError(status, 'monolog.error_lectura') : '',
            icon: 'error',
          });
        });
    });
  }

  handlePageChange(delta) {
    var nuevaPagina = this.state.page + delta;
    if (nuevaPagina < 1) {
      return;
    }
    var totalPaginas = Math.max(1, Math.ceil(this.state.total / PER_PAGE));
    if (nuevaPagina > totalPaginas) {
      return;
    }
    this.setState({ page: nuevaPagina }, this.cargarLineas);
  }

  formatBytes(n) {
    if (!n || n <= 0) return '0 B';
    var units = ['B', 'KB', 'MB', 'GB'];
    var i = 0;
    var v = n;
    while (v >= 1024 && i < units.length - 1) {
      v = v / 1024;
      i++;
    }
    return v.toFixed(v < 10 && i > 0 ? 1 : 0) + ' ' + units[i];
  }

  // Para la metadata del listado de archivos: DD/MM/YYYY HH:mm en TZ Argentina.
  formatFecha(iso) {
    if (!iso) return '';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return FMT_FECHA.format(d) + ' ' + FMT_HORA_CORTA.format(d);
    } catch (e) {
      return iso;
    }
  }

  // Para las filas de log: DD/MM/YYYY HH:mm:ss en TZ Argentina.
  formatTimestampLog(iso) {
    if (!iso) return '—';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return FMT_FECHA.format(d) + ' ' + FMT_HORA.format(d);
    } catch (e) {
      return iso;
    }
  }

  renderListaArchivos() {
    if (this.state.filesCargando) {
      return (
        <div className="monolog-files-empty">
          <Loading />
        </div>
      );
    }
    if (this.state.filesError) {
      return <div className="monolog-files-empty text-danger">{this.state.filesError}</div>;
    }
    if (!this.state.files || this.state.files.length === 0) {
      return (
        <div className="monolog-files-empty">
          <FormattedMessage id="monolog.lista_vacia" defaultMessage="No hay archivos de log disponibles." />
        </div>
      );
    }
    var self = this;
    return (
      <ul className="monolog-files-list">
        {this.state.files.map(function (f) {
          var activo = self.state.archivoSeleccionado === f.name;
          return (
            <li
              key={f.name}
              className={'monolog-file-item' + (activo ? ' monolog-file-item-active' : '')}
              onClick={function () { self.seleccionarArchivo(f.name); }}
            >
              <div className="monolog-file-row">
                <div className="monolog-file-info">
                  <div className="monolog-file-name">
                    <i className="fa fa-file-text-o mr-1"></i> {f.name}
                  </div>
                  <div className="monolog-file-meta">
                    {self.formatFecha(f.modifiedAt)} · {self.formatBytes(f.size)}
                  </div>
                </div>
                <button
                  type="button"
                  className="monolog-file-delete"
                  title={self.props.intl.formatMessage({ id: 'monolog.eliminar', defaultMessage: 'Eliminar' })}
                  onClick={function (e) { self.handleEliminar(f.name, e); }}
                >
                  <i className="fa fa-trash-o"></i>
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  renderLineaNivel(level) {
    if (!level) {
      return <span className="monolog-level monolog-level-RAW">RAW</span>;
    }
    return <span className={'monolog-level monolog-level-' + level}>{level}</span>;
  }

  renderLineas() {
    if (!this.state.archivoSeleccionado) {
      return (
        <div className="monolog-viewer-empty">
          <FormattedMessage id="monolog.seleccionar_archivo" defaultMessage="Seleccioná un archivo de la izquierda para ver su contenido." />
        </div>
      );
    }
    if (this.state.lineasCargando) {
      return (
        <div className="monolog-viewer-empty">
          <Loading />
        </div>
      );
    }
    if (this.state.lineasError) {
      return <div className="monolog-viewer-empty text-danger">{this.state.lineasError}</div>;
    }
    if (!this.state.lineas || this.state.lineas.length === 0) {
      return (
        <div className="monolog-viewer-empty">
          <FormattedMessage id="monolog.sin_resultados" defaultMessage="Sin líneas que coincidan con los filtros." />
        </div>
      );
    }
    var self = this;
    return (
      <table className="monolog-table">
        <thead>
          <tr>
            <th style={{ width: 180 }}>
              <FormattedMessage id="monolog.col_timestamp" defaultMessage="Timestamp" />
            </th>
            <th style={{ width: 90 }}>
              <FormattedMessage id="monolog.col_nivel" defaultMessage="Nivel" />
            </th>
            <th>
              <FormattedMessage id="monolog.col_mensaje" defaultMessage="Mensaje" />
            </th>
          </tr>
        </thead>
        <tbody>
          {this.state.lineas.map(function (line, i) {
            return (
              <tr key={i} className={'monolog-row ' + (line.level ? 'monolog-row-' + line.level : 'monolog-row-RAW')}>
                <td className="monolog-cell-ts" title={line.ts || ''}>{self.formatTimestampLog(line.ts)}</td>
                <td>{self.renderLineaNivel(line.level)}</td>
                <td className="monolog-cell-msg"><MonologMessage message={line.message} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  renderPaginacion() {
    if (!this.state.archivoSeleccionado || this.state.lineasError) {
      return null;
    }
    var total = this.state.total;
    var page = this.state.page;
    var totalPaginas = Math.max(1, Math.ceil(total / PER_PAGE));
    return (
      <div className="monolog-pagination">
        <button
          className="btn btn-sm btn-outline-secondary"
          disabled={page <= 1}
          onClick={() => this.handlePageChange(-1)}
        >
          <i className="fa fa-chevron-left"></i>
        </button>
        <span className="monolog-pagination-info">
          <FormattedMessage
            id="monolog.paginacion"
            defaultMessage="Página {page} de {total} ({rows} líneas)"
            values={{ page: page, total: totalPaginas, rows: total }}
          />
        </span>
        <button
          className="btn btn-sm btn-outline-secondary"
          disabled={page >= totalPaginas}
          onClick={() => this.handlePageChange(1)}
        >
          <i className="fa fa-chevron-right"></i>
        </button>
        {this.state.truncated && (
          <span className="badge badge-warning ml-2">
            <FormattedMessage id="monolog.truncado" defaultMessage="Archivo grande: solo se carga la cola." />
          </span>
        )}
      </div>
    );
  }

  render() {
    if (this.state.redirectTo) {
      return <Redirect to={this.state.redirectTo} />;
    }
    var self = this;
    return (
      <div className="content-wrapper">
        <div className="content-header row">
          <div className="content-header-left col-md-6 col-12 mb-2">
            <h3 className="content-header-title">
              <FormattedMessage id="monolog.titulo" defaultMessage="Logs (Monolog)" />
            </h3>
          </div>
        </div>
        <div className="content-body">
          <div className="monolog-container">
            <aside className="monolog-sidebar">
              <div className="monolog-sidebar-header">
                <strong>
                  <FormattedMessage id="monolog.archivos" defaultMessage="Archivos" />
                </strong>
                <button
                  className="btn btn-sm btn-link p-0"
                  onClick={this.cargarArchivos}
                  title={this.props.intl.formatMessage({ id: 'monolog.refrescar', defaultMessage: 'Refrescar' })}
                >
                  <i className="fa fa-refresh"></i>
                </button>
              </div>
              {this.renderListaArchivos()}
            </aside>

            <section className="monolog-viewer">
              <div className="monolog-toolbar">
                <div className="monolog-niveles">
                  {NIVELES.map(function (n) {
                    var activo = self.state.filtroNiveles.indexOf(n) >= 0;
                    return (
                      <label key={n} className={'monolog-nivel-toggle' + (activo ? ' monolog-nivel-toggle-active monolog-nivel-' + n : '')}>
                        <input
                          type="checkbox"
                          checked={activo}
                          onChange={function () { self.handleNivelToggle(n); }}
                        />
                        <span>{n}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="monolog-busqueda">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={this.props.intl.formatMessage({
                      id: 'monolog.placeholder_busqueda',
                      defaultMessage: 'Buscar texto...',
                    })}
                    value={this.state.filtroBusqueda}
                    onChange={this.handleBusquedaChange}
                  />
                </div>
              </div>
              <div className="monolog-table-wrapper">
                {this.renderLineas()}
              </div>
              {this.renderPaginacion()}
            </section>
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(Monolog);
